import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CATEGORIES_COUT, ETAPES_COUT } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
import { PiecesJointes } from './PiecesJointes';
interface Cout {
  id: string;
  libelle: string;
  categorie: keyof typeof CATEGORIES_COUT;
  etape: keyof typeof ETAPES_COUT;
  montant: string;
  cree_par: string;
  version: number;
}
const monnaie = (v: string) => `${BigInt(v).toLocaleString('fr-FR')} XAF`;
function Historique({ id }: { id: string }) {
  const q = useQuery({
    queryKey: ['couts', 'historique', id],
    queryFn: () =>
      requete<{
        elements: {
          id: string;
          etape: keyof typeof ETAPES_COUT;
          montant: string;
          motif: string;
          cree_le: string;
        }[];
      }>(`/couts-reseau/${id}/historique`),
  });
  return (
    <>
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      <ol>
        {q.data?.elements.map((h) => (
          <li key={h.id}>
            {ETAPES_COUT[h.etape]} · {monnaie(h.montant)} · {h.motif}
          </li>
        ))}
      </ol>
    </>
  );
}
export function PageCouts() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [historique, setHistorique] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const q = useQuery({
    queryKey: ['couts', 'liste'],
    queryFn: () =>
      requete<{
        elements: Cout[];
        totaux: { etape: keyof typeof ETAPES_COUT; montant: string; nombre: number }[];
      }>('/couts-reseau'),
    enabled: peut(contexte, 'cout', 'voir'),
  });
  const missions = useQuery({
    queryKey: ['interventions-reseau'],
    queryFn: () => requete<{ elements: { id: string; titre: string }[] }>('/interventions-reseau'),
    enabled: creation && peut(contexte, 'intervention', 'voir'),
  });
  const equipements = useQuery({
    queryKey: ['patrimoine', 'equipements'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; code: string }[] }>('/patrimoine/equipements'),
    enabled: creation && peut(contexte, 'patrimoine', 'voir'),
  });
  if (!peut(contexte, 'cout', 'voir')) return <p role="alert">Accès aux coûts non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Coûts d’exploitation" />
      {peut(contexte, 'cout', 'exporter') && (
        <button
          disabled={enCours}
          onClick={async () => {
            setErreur('');
            setEnCours(true);
            try {
              const r = await requete<Response>('/couts-reseau/export', { brut: true });
              const url = URL.createObjectURL(await r.blob());
              const a = document.createElement('a');
              a.href = url;
              a.download = 'couts-reseau.csv';
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (e) {
              setErreur(messageErreur(e));
            } finally {
              setEnCours(false);
            }
          }}
        >
          Exporter tous les coûts CSV (10 000 maximum)
        </button>
      )}
      <p>
        Suivi en francs CFA (XAF). Les étapes représentent l’état courant de chaque coût et ne
        doivent pas être additionnées comme des dépenses distinctes. Aucun paiement n’est déclenché
        ici.
      </p>
      {peut(contexte, 'cout', 'creer') && (
        <button onClick={() => setCreation(!creation)}>Saisir un coût</button>
      )}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 700 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setErreur('');
            setEnCours(true);
            try {
              await requete('/couts-reseau', {
                methode: 'POST',
                corps: {
                  libelle: d.get('libelle'),
                  categorie: d.get('categorie'),
                  montant: Number(d.get('montant')),
                  intervention_id: d.get('intervention_id') || null,
                  equipement_id: d.get('equipement_id') || null,
                },
              });
              setCreation(false);
              await client.invalidateQueries({ queryKey: ['couts'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Libellé <input name="libelle" required minLength={3} maxLength={200} />
          </label>
          <label>
            Catégorie{' '}
            <select name="categorie">
              {Object.entries(CATEGORIES_COUT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Montant estimé en XAF{' '}
            <input name="montant" type="number" required min={0} max={100000000000} step={1} />
          </label>
          <label>
            Intervention{' '}
            <select name="intervention_id">
              <option value="">Aucune</option>
              {missions.data?.elements.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.titre}
                </option>
              ))}
            </select>
          </label>
          <label>
            Équipement{' '}
            <select name="equipement_id">
              <option value="">Aucun</option>
              {equipements.data?.elements.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} — {e.nom}
                </option>
              ))}
            </select>
          </label>
          <p>Choisissez au moins une intervention ou un équipement.</p>
          <button disabled={enCours}>Enregistrer l’estimation</button>
          {(missions.error || equipements.error) && (
            <p role="alert">{messageErreur(missions.error || equipements.error)}</p>
          )}
        </form>
      )}
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement…</p>}
      <ul>
        {q.data?.totaux.map((t) => (
          <li key={t.etape}>
            {ETAPES_COUT[t.etape]} : {monnaie(t.montant)} ({t.nombre} coût(s))
          </li>
        ))}
      </ul>
      <p>Liste limitée aux 500 coûts les plus récents ; totaux sur l’ensemble de l’organisation.</p>
      {q.data?.elements.map((c) => {
        const auteur = c.cree_par === contexte?.compte.id;
        const actions: { code: string; nom: string }[] = [];
        if (auteur && peut(contexte, 'cout', 'creer')) {
          if (c.etape === 'ESTIMATED')
            actions.push(
              { code: 'reviser', nom: 'Réviser le montant' },
              { code: 'engager', nom: 'Enregistrer l’engagement' },
            );
          if (c.etape === 'COMMITTED')
            actions.push({ code: 'facturer', nom: 'Enregistrer la facture' });
          if (!['VALIDATED', 'CANCELLED'].includes(c.etape))
            actions.push({ code: 'annuler', nom: 'Annuler le coût' });
        }
        if (!auteur && peut(contexte, 'cout', 'valider') && c.etape === 'INVOICED')
          actions.push(
            { code: 'valider', nom: 'Valider le coût' },
            { code: 'rejeter', nom: 'Demander une correction' },
          );
        return (
          <article key={c.id} style={{ paddingBlock: 20, borderBottom: '1px solid currentColor' }}>
            <h2>{c.libelle}</h2>
            <p>
              {ETAPES_COUT[c.etape]} · {monnaie(c.montant)} · {CATEGORIES_COUT[c.categorie]}
            </p>
            {actions.length > 0 && (
              <form
                key={c.version}
                style={{ display: 'grid', gap: 12, maxWidth: 700 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const d = new FormData(e.currentTarget);
                  setErreur('');
                  setEnCours(true);
                  try {
                    await requete(`/couts-reseau/${c.id}/actions`, {
                      methode: 'POST',
                      corps: {
                        action: d.get('action'),
                        version: c.version,
                        motif: d.get('motif'),
                        ...(d.get('action') === 'reviser'
                          ? { montant: Number(d.get('montant')) }
                          : {}),
                      },
                    });
                    await client.invalidateQueries({ queryKey: ['couts'] });
                  } catch (err) {
                    setErreur(messageErreur(err));
                  } finally {
                    setEnCours(false);
                  }
                }}
              >
                <label>
                  Action{' '}
                  <select name="action">
                    {actions.map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.nom}
                      </option>
                    ))}
                  </select>
                </label>
                {c.etape === 'ESTIMATED' && (
                  <label>
                    Montant révisé en XAF{' '}
                    <input
                      name="montant"
                      type="number"
                      min={0}
                      max={100000000000}
                      step={1}
                      defaultValue={c.montant}
                    />
                  </label>
                )}
                <label>
                  Motif ou référence justificative{' '}
                  <input name="motif" required minLength={3} maxLength={1000} />
                </label>
                <button disabled={enCours}>Appliquer l’action</button>
              </form>
            )}
            <button
              aria-expanded={historique === c.id}
              onClick={() => setHistorique(historique === c.id ? null : c.id)}
            >
              Historique du coût
            </button>
            {historique === c.id && <Historique id={c.id} />}
            <PiecesJointes
              type="cout"
              id={c.id}
              ecriture={
                auteur &&
                peut(contexte, 'cout', 'creer') &&
                ['ESTIMATED', 'COMMITTED'].includes(c.etape)
              }
            />
          </article>
        );
      })}
    </section>
  );
}
